import React from "react"
import axios from "axios"
import { useState, useEffect } from "react"
import { API_ROUTES, APP_ROUTES } from "../utils/constants"
import { useNavigate, useLocation } from "react-router-dom"
import authService from "../services/authService"
import { toast } from "react-toastify"
import { useAuth } from "../lib/useAuth"
import { authenticate } from "../services/userApis"
import { useSocket } from "../contexts/SocketContext"

const SignIn = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { initializeSocket } = useSocket()

    const { user, isAuthenticated, login } = useAuth()

    const [isLoading, setIsLoading] = useState(false)
    const [formData, setFormData] = useState({
        username: "",
        password: "",
    })
    const [errors, setErrors] = useState({})
    const [showPassword, setShowPassword] = useState(false)

    // Redirect if already authenticated
    useEffect(() => {
        if (user || isAuthenticated) {
            const from = location.state?.from?.pathname || APP_ROUTES.INDEX
            navigate(from, { replace: true })
        }
    }, [user, isAuthenticated, location.state?.from?.pathname])

    // Don't render if already authenticated
    if (user || isAuthenticated) {
        return null
    }

    // Form validation
    const validateForm = () => {
        const newErrors = {}

        if (!formData.username.trim()) {
            newErrors.username = "نام کاربری الزامی است"
        }

        if (!formData.password) {
            newErrors.password = "رمز عبور الزامی است"
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }))

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }))
        }
    }

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault()

        // Validate form
        if (!validateForm()) {
            return
        }

        try {
            setIsLoading(true)
            setErrors({})

            let data = {
                username: formData.username,
                password: formData.password,
            }

            const response = await authenticate(data)

            if (!response?.data?.access_token) {
                toast.error("خطا در دریافت توکن احراز هویت")
                return
            }

            // Use the login function from useAuth
            const loginSuccess = await login({
                token: response.data.access_token,
                refreshToken: response.data.refresh_token,
                userData: response.data.user,
                expiresIn: response.data.expires_in,
            })

            if (loginSuccess) {
                // Show success message
                toast.success("ورود موفقیت‌آمیز بود")

                initializeSocket()

                // Navigate to the intended page or default
                const from = location.state?.from?.pathname || APP_ROUTES.INDEX
                navigate(from, { replace: true })
            }
        } catch (err) {
            console.error("Sign in error:", err)

            if (err?.response?.status === 401) {
                toast.error("نام کاربری یا رمز عبور اشتباه میباشد")
                setErrors({ password: "نام کاربری یا رمز عبور اشتباه میباشد" })
            } else if (err?.response?.status === 403) {
                toast.error("حساب کاربری شما غیرفعال شده است")
                setErrors({ username: "حساب کاربری غیرفعال" })
            } else if (err?.code === "ECONNABORTED") {
                toast.error("اتصال به سرور برقرار نشد. لطفاً دوباره تلاش کنید")
            } else if (err?.response?.status >= 500) {
                toast.error("خطای سرور. لطفاً بعداً تلاش کنید")
            } else {
                toast.error("خطا در ورود. لطفاً دوباره تلاش کنید")
            }
        } finally {
            setIsLoading(false)
        }
    }

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handleSubmit(e)
        }
    }

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <img src="assets/img/logo.png" alt="Logo" className="login-logo" />
                    <h1 className="login-title">ورود به سیستم</h1>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    <div className="login-input__holder">
                        <div className="input-group">
                            <label htmlFor="username" className="input-label">
                                نام کاربری
                            </label>
                            <input
                                name="username"
                                id="username"
                                type="text"
                                className={`styled-input ${errors.username ? "error" : ""}`}
                                value={formData.username}
                                onChange={handleInputChange}
                                onKeyPress={handleKeyPress}
                                disabled={isLoading}
                                autoComplete="username"
                                placeholder="نام کاربری خود را وارد کنید"
                            />
                            {errors.username && <span className="error-message">{errors.username}</span>}
                        </div>

                        <div className="input-group">
                            <label htmlFor="password" className="input-label">
                                کلمه عبور
                            </label>
                            <div className="password-input-container">
                                <input
                                    name="password"
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    className={`styled-input ${errors.password ? "error" : ""}`}
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    onKeyUp={handleKeyPress}
                                    disabled={isLoading}
                                    autoComplete="current-password"
                                    placeholder="رمز عبور خود را وارد کنید"
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    disabled={isLoading}
                                >
                                    {showPassword ? "🔒" : "👁️"}
                                </button>
                            </div>
                            {errors.password && <span className="error-message">{errors.password}</span>}
                        </div>
                    </div>

                    <button
                        type="submit"
                        className={`styled-button login-button ${isLoading ? "loading" : ""}`}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <div className="loading-spinner">
                                <div className="spinner"></div>
                                <span>در حال ورود...</span>
                            </div>
                        ) : (
                            <span>ورود</span>
                        )}
                    </button>
                </form>

                <div className="login-footer">
                    <p className="help-text">در صورت فراموشی رمز عبور با مدیر سیستم تماس بگیرید</p>
                </div>
            </div>
        </div>
    )
}

export default SignIn
